<script setup lang="ts">
    import { useEvent } from '@/composables/useEventBus';
    import { useIsMobileLayout } from '~/composables/useIsMobileLayout';
    import useDesignSystemField from '~/composables/useDesignSystemField';
    import useWasteCenterForm from '~/composables/useWasteCenterForm';

    // Below `lg` this is a drawer, with the fields and the primary every drawer of the
    // document form wears; desktop keeps its modal. The mobile waste settings do not open
    // it at all: they push the same form as a panel of their own sheet
    // (`Form/mobile/sheets/WasteCenterSheet.vue`), rather than stacking a second drawer.
    const isMobile = useIsMobileLayout();
    const { fieldClass } = useDesignSystemField();
    const MOBILE_FIELD_CLASS = fieldClass();

    const { form, submit: submitForm } = useWasteCenterForm();
    const { modalName } = useModal('common:waste-center:form-modal', () => {
        form.reset();
    });

    const submit = async () => {
        if (await submitForm()) close();
    };

    const close = () => {
        form.reset();
        useEvent(`common:waste-center:form-modal:close`);
    };
</script>

<template>
    <!-- `UiResponsiveModal` mounts the same `Modal` desktop always had (same name, same
         `centered` style, `scrollable`), so nothing changes there. -->
    <UiResponsiveModal
        :name="modalName"
        :scrollable="true"
        :title="$t('worksite.worksite.waste_center.create_form.title')"
        @before-close="form.reset()"
    >
        <div
            v-if="!isMobile"
            :class="$theme('modal.title.container')"
        >
            <h2 :class="$theme('modal.title.text')">
                {{ $t('worksite.worksite.waste_center.create_form.title') }}
            </h2>
        </div>
        <form @submit.prevent="submit">
            <div :class="[$theme('modal.padding'), isMobile ? 'pt-0 pb-2' : '']">
                <FormInput
                    :form="form"
                    input-name="title"
                    :class-name="isMobile ? MOBILE_FIELD_CLASS : undefined"
                />
                <FormWasteCenterTypeSelect
                    :form="form"
                    input-name="centerType"
                    :with-null-option="true"
                    :class-name="isMobile ? MOBILE_FIELD_CLASS : undefined"
                />

                <FormAddressAutocomplete
                    :form="form"
                    input-name="address"
                    :class-name="isMobile ? MOBILE_FIELD_CLASS : undefined"
                />

                <div
                    v-if="isMobile"
                    class="mt-6"
                >
                    <UiAction
                        :content="{ label: 'actions.save', type: 'brand', icon: 'fa-regular fa-check' }"
                        modifier="dsFullLg"
                        shape="softLg"
                        :is-loading="form.isLoading"
                        @click="submit"
                    />
                </div>
                <div
                    v-else
                    :class="$theme('modal.footer.button_container')"
                >
                    <LoadingButton
                        :extra-class-name="$theme('modal.footer.button')"
                        :class-name="$theme('button.style.cancel')"
                        prevent-default
                        @clicked="close()"
                        >{{ $t('actions.cancel') }}</LoadingButton
                    >
                    <LoadingButton
                        :extra-class-name="$theme('modal.footer.button')"
                        :loading="form.isLoading"
                        type="submit"
                        >{{ $t('actions.save') }}</LoadingButton
                    >
                </div>
            </div>
        </form>
    </UiResponsiveModal>
</template>
